import { useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomTabs from "../../components/CustomTabs/CustomTabs";
import Deals from "../Deals/Deals";
import JointVenturesDeals from "../JointVenturesDeals/JointVenturesDeals";
import { getDealCategories } from "../../redux/slices/dealUploadSlice";
import { count } from "../../redux/slices/dealsSlice";
import { DEAL_STATUS, DEAL_CATEGORY_IDS } from "../../constants/constants";
import { PERM_CODE } from "../../constants/PermissionCodes";
import useModuleContext from "../../hooks/useModuleContext";
import { clearSavedContext, updateUrlParams } from "../../helpers/moduleContext";
import "./DealsManagement.scss";

/** Action Count: active / in-process deals only (same rule for IF, SCF, JV). */
const ACTION_COUNT_STATUS_IDS = new Set([
  DEAL_STATUS.PENDING.ID,
  DEAL_STATUS.APPROVED.ID,
  DEAL_STATUS.FUNDED.ID,
  DEAL_STATUS.PENDING_DISBURSEMENT.ID,
  DEAL_STATUS.DISBURSED.ID,
  DEAL_STATUS.PARTIALLY_SETTLED.ID,
]);

const getCategoryDealsTotal = (countData, categoryId) => {
  if (!categoryId || !Array.isArray(countData)) {
    return 0;
  }

  return countData
    .filter(
      (item) =>
        item.dealCategoryId === categoryId &&
        ACTION_COUNT_STATUS_IDS.has(item.statusId)
    )
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
};

const DealsManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation() || {};
  const initialTab = location?.state?.activeTab || "deals";

  const {
    activeTab,
    handleTabChange,
  } = useModuleContext({
    moduleKey: "dealsManagement",
    defaultTab: initialTab,
  });

  const { dealCategories = [] } = useSelector(
    (state) => state?.dealUpload || {},
  );
  const { CountData = [] } = useSelector((state) => state?.deals || {});
  const { user } = useSelector((state) => state?.auth || {});

  const hasDealUploadPrivilege = useMemo(() => {
    const raw = user?.privilege || [];
    const privilegeCodes = raw
      .map((p) => (typeof p === "string" ? p : p?.privilegeCode))
      .filter(Boolean);
    return privilegeCodes.includes(PERM_CODE.IUACC);
  }, [user]);

  const invoiceFinancingCategoryId = useMemo(
    () =>
      dealCategories.find(
        (item) => item?.dealCategoryName === "Invoice Financing",
      )?.dealCategoryId || null,
    [dealCategories],
  );
  const supplyChainCategoryId = useMemo(
    () =>
      dealCategories.find(
        (item) =>
          item?.dealCategoryId === DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE ||
          item?.dealCategoryName === "Supply Chain Financing" ||
          item?.dealCategoryName === "Supply Chain Finance",
      )?.dealCategoryId || null,
    [dealCategories],
  );
  const jointVenturesCategoryId = useMemo(
    () =>
      dealCategories.find((item) => item?.dealCategoryName === "Joint Ventures")
        ?.dealCategoryId || null,
    [dealCategories],
  );

  const onCategoryTabChange = useCallback(
    (newTab) => {
      // Option B: Reset category filters on category tab click so the new category starts fresh with default status
      updateUrlParams({
        tab: newTab,
        if_status: null,
        if_page: null,
        if_search: null,
        scf_status: null,
        scf_page: null,
        scf_search: null,
        jv_status: null,
        jv_page: null,
        jv_search: null,
      });

      clearSavedContext(`deals_${invoiceFinancingCategoryId || "DCT1000001"}`);
      clearSavedContext(`deals_${supplyChainCategoryId || "DCT1000006"}`);
      clearSavedContext("deals_JV");

      handleTabChange(newTab);
    },
    [handleTabChange, invoiceFinancingCategoryId, supplyChainCategoryId]
  );

  useEffect(() => {
    if (location?.state?.activeTab) {
      onCategoryTabChange(location.state.activeTab);
    }
  }, [location?.state?.activeTab, onCategoryTabChange]);

  useEffect(() => {
    dispatch(getDealCategories());
    dispatch(count("DEALS"));
  }, [dispatch]);

  const invoiceDealsCount = useMemo(
    () => getCategoryDealsTotal(CountData, invoiceFinancingCategoryId),
    [CountData, invoiceFinancingCategoryId],
  );

  const supplyChainDealsCount = useMemo(
    () => getCategoryDealsTotal(CountData, supplyChainCategoryId),
    [CountData, supplyChainCategoryId],
  );

  const jvDealsCount = useMemo(
    () => getCategoryDealsTotal(CountData, jointVenturesCategoryId),
    [CountData, jointVenturesCategoryId],
  );

  const tabItems = useMemo(
    () => [
      {
        key: "deals",
        label: `Invoice Financing (${invoiceDealsCount})`,
        children: (
          <Deals
            dealCategoryId={invoiceFinancingCategoryId || DEAL_CATEGORY_IDS.INVOICE_FINANCING}
            dealCategoryName="Invoice Financing"
            paramPrefix="if"
            onDealUpload={() => navigate("/invoice-upload")}
            hasDealUploadPrivilege={hasDealUploadPrivilege}
          />
        ),
      },
      {
        key: "supply-chain-deals",
        label: `Supply Chain Financing (${supplyChainDealsCount})`,
        children: (
          <Deals
            dealCategoryId={supplyChainCategoryId || DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE}
            dealCategoryName="Supply Chain Financing"
            paramPrefix="scf"
            onDealUpload={() => navigate("/supply-chain-upload")}
            hasDealUploadPrivilege={hasDealUploadPrivilege}
          />
        ),
      },
      {
        key: "joint-ventures-deals",
        label: `Joint Ventures (${jvDealsCount})`,
        children: (
          <JointVenturesDeals
            paramPrefix="jv"
            onDealUpload={() => navigate("/joint-ventures")}
            hasDealUploadPrivilege={hasDealUploadPrivilege}
          />
        ),
      },
    ],
    [
      invoiceDealsCount,
      invoiceFinancingCategoryId,
      supplyChainDealsCount,
      supplyChainCategoryId,
      jvDealsCount,
      hasDealUploadPrivilege,
      navigate,
    ],
  );

  return (
    <div className="deals-management-page">
      <CustomTabs
        items={tabItems}
        activeKey={activeTab}
        onChange={onCategoryTabChange}
        destroyInactiveTabPane={true}
        className="deals-management-tabs"
      />
    </div>
  );
};

export default DealsManagement;
